import React, { ChangeEventHandler, ComponentProps, KeyboardEventHandler, MouseEventHandler, useEffect, useMemo, useRef, useState } from 'react'

import { scrollbarStyle } from '@/shared/styles'
import { faChevronDown, faSearch, faXmark, IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import { Badge, ClickAwayListener, Grid, InputAdornment, MenuItem, MenuList, Popper, styled, Typography } from '@mui/material'

import { Button } from '../Button/Button'
import { Icon } from '../Icon/Icon'
import { IconButton } from '../IconButton/IconButton'
import { TextField } from '../TextField/TextField'
import { FilterMultiselectDropdownItem } from './FilterMultiselectDropdownItem'

export type DropdownOption = {
  label: string
  value: string | number
}

export type FilterMultiselectDropdownProps = ComponentProps<typeof Button> & {
  children?: React.ReactNode
  label: string
  values: DropdownOption[]
  options: DropdownOption[]
  onSelectItem: (option: DropdownOption) => void
  error?: boolean
  disabled?: boolean
  searchActionEnable?: boolean
  searchActionIcon?: IconDefinition
  slotProps?: {
    search?: ComponentProps<typeof TextField>
    searchAction?: ComponentProps<typeof IconButton>
    popper?: ComponentProps<typeof Popper>
  }
  closeAfterSelect?: boolean
}

export const FilterMultiselectDropdown: React.FC<FilterMultiselectDropdownProps> = ({ children, label, values, searchActionEnable, searchActionIcon, options, error, disabled, onSelectItem, closeAfterSelect, slotProps, ...props }) => {
  const menuListRef = useRef<HTMLUListElement>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)
  const [width, setWidth] = useState(0)
  const [searchText, setSearchText] = useState('')

  // Handle Popper Opens
  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation()
    event.preventDefault()

    setAnchorEl(event.currentTarget)
    setOpen((prevOpen) => !prevOpen)
  }

  const onKeyDown: KeyboardEventHandler<HTMLButtonElement> = (event) => {
    if (event.key === 'ArrowDown') {
      setAnchorEl(event.currentTarget)
      setOpen(true)

      if (open && menuListRef) {
        menuListRef?.current?.focus()
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClose = (event: any) => {
    event.stopPropagation()
    event.preventDefault()

    setTimeout(() => {
      setOpen(false)
    }, 100)
  }

  // Handle Search
  const handleSearch: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    event.stopPropagation()

    setSearchText(event.target.value)
  }

  const filteredOptions = useMemo(() => {
    return options.filter((option) => {
      return option.label.includes(searchText) || option.label.toLowerCase().includes(searchText) || option.value.toString().includes(searchText)
    })
  }, [options, searchText])

  useEffect(() => {
    if (anchorEl) {
      setWidth(anchorEl.offsetWidth)
    }
  }, [anchorEl])

  return (
    <>
      <StyledButton text onClick={handleClick} onKeyDown={onKeyDown} $open={open} $error={error} disabled={disabled} {...props}>
        <StyledTitleContainer container justifyContent={'space-between'} wrap={'nowrap'}>
          <div>
            {label}
            {values.length > 0 && <StyledBadge badgeContent={values.length} color="secondary" />}
          </div>
          <Grid item sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <StyledIcon icon={faChevronDown} />
          </Grid>
        </StyledTitleContainer>
      </StyledButton>
      <StyledPopper
        $width={width}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        {...slotProps?.popper}
      >
        <ClickAwayListener onClickAway={handleClose} mouseEvent="onMouseDown" touchEvent="onTouchStart">
          <StyledMenuList ref={menuListRef}>
            <MenuItem
              disableRipple
              disableTouchRipple
              disableGutters
              style={{ backgroundColor: 'transparent' }}
              onKeyDown={e => e.stopPropagation()}
            >
              <StyledTextField
                autoFocus
                onChange={handleSearch}
                defaultValue={searchText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <StyledIcon icon={faSearch} />
                    </InputAdornment>
                  ),
                }}
                {...slotProps?.search}
              />
              {searchActionEnable && (
                <StyledSearchActionButton {...slotProps?.searchAction}>
                  <Icon sx={{ fontSize: '16px' }} icon={searchActionIcon ?? faXmark} />
                </StyledSearchActionButton>
              )}
            </MenuItem>
            {filteredOptions.map((option, index) => (
              <FilterMultiselectDropdownItem
                key={index}
                value={option}
                isSelected={values.some((value) => value.value === option.value)}
                disabled={disabled}
                onSelect={(ev) => {
                  onSelectItem(option)

                  if (closeAfterSelect) {
                    handleClose(ev)
                  }
                }}
              >
                {option.label}
              </FilterMultiselectDropdownItem>
            ))}
          </StyledMenuList>
        </ClickAwayListener>
      </StyledPopper>
    </>
  )
}


export const StyledButton = styled(Button, { shouldForwardProp: (prop) => prop !== '$open' && prop !== '$error' && prop !== '$disabled' }) <{ $open: boolean, $error?: boolean, $disabled?: boolean }>`
  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

  ${(props) => props.$open && `
  background-color: ${props.theme.palette.action.hover};
  `}

  ${(props) => props.$error && `
    border: 1px solid ${props.theme.palette.error.main};
  `}
`

const StyledBadge = styled(Badge)`
  margin-left: ${(props) => props.theme.typography.pxToRem(12)};
  margin-right: ${(props) => props.theme.typography.pxToRem(8)};
  margin-bottom: ${(props) => props.theme.typography.pxToRem(2)};

  & .MuiBadge-badge {
    min-width: ${(props) => props.theme.typography.pxToRem(16)};
    width: ${(props) => props.theme.typography.pxToRem(16)};
    min-height: ${(props) => props.theme.typography.pxToRem(16)};
    height: ${(props) => props.theme.typography.pxToRem(16)};
  }
`

const StyledTextField = styled(TextField)`
  margin: ${(props) => props.theme.typography.pxToRem(4)} ${(props) => props.theme.typography.pxToRem(4)};
  width: 100%;
  
  & .MuiOutlinedInput-root input {
    padding-left: 0 !important;
  }

  &.MuiOutlinedInput-root {
    padding: 0;

  }


  .Mui-focused fieldset {
    border-color: ${(props) => props.theme.palette.text.primary};
    box-shadow: none;
  }

  input {
    padding-left: 0;
  }
`

const StyledSearchActionButton = styled(IconButton)`
  margin: 0;
`

export const StyledTagsContainer = styled(Grid)`
  display: flex;
  justify-content: flex-start;
  overflow: auto;

  ${scrollbarStyle};
`

export const StyledPopper = styled(Popper, { shouldForwardProp: (prop) => prop !== '$width' }) <{ $width: number }>`
  min-width: ${(props) => props.$width}px;
  width: fit-content;
  z-index: ${(props) => props.theme.zIndex.appBar + 110};
  background-color: ${(props) => props.theme.palette.background.paper};
  border-radius: ${(props) => props.theme.typography.pxToRem(8)};
`

export const StyledDropdownContainer = styled('div')`
  display: inline-block;
`

export const StyledIcon = styled(Icon)`
  color: ${props => props.theme.palette.text.primary};
  margin-left: ${props => props.theme.typography.pxToRem(4)};
  font-size: ${props => props.theme.typography.pxToRem(14)};
`

export const StyledTitleContainer = styled(Grid)`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  text-overflow: ellipsis;
`

export const StyledDropdownValue = styled(Typography)`
  color: ${props => props.theme.palette.text.primary};
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const StyledMenuList = styled(MenuList)`
  display: flex;
  flex-direction: column;
  border-radius: ${props => props.theme.typography.pxToRem(8)};
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.15);
  gap: 0;
  padding-top: 0;
  padding-bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  ${scrollbarStyle};
  max-height: 300px;

  li:last-of-type {
    &:after {
      border-radius: 0 0 ${props => props.theme.typography.pxToRem(8)} ${props => props.theme.typography.pxToRem(8)};
    }
  }
  
  ${props => props.theme.breakpoints.up('sm')} {
    gap: ${props => props.theme.typography.pxToRem(4)};
  }
`
